import { alpha, keyframes, styled } from "@mui/material/styles";
import TextField from "../components/TextField";
import { VALUES } from "../constants";
import { PRIMARY, SUCCESS, ERROR, INFO, GREY } from "../theme/palette";

export function bgBlur(props: any) {
  const color = props?.color || "#000000";
  const blur = props?.blur || 6;
  const opacity = props?.opacity || 0.8;
  const imgUrl = props?.imgUrl;

  if (imgUrl) {
    return {
      position: "relative",
      backgroundImage: `url(${imgUrl})`,
      "&:before": {
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 9,
        content: '""',
        width: "100%",
        height: "100%",
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`,
        backgroundColor: alpha(color, opacity),
      },
    };
  }

  return {
    backdropFilter: `blur(${blur}px)`,
    WebkitBackdropFilter: `blur(${blur}px)`,
    backgroundColor: alpha(color, opacity),
  };
}

export function bgGradient(props: any) {
  const direction = props?.direction || "to bottom";
  const startColor = props?.startColor;
  const endColor = props?.endColor;
  const imgUrl = props?.imgUrl;
  const color = props?.color;

  if (imgUrl) {
    return {
      background: `linear-gradient(${direction}, ${startColor || color}, ${
        endColor || color
      }), url(${imgUrl})`,
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center center",
    };
  }

  return {
    background: `linear-gradient(${direction}, ${startColor}, ${endColor})`,
  };
}

export function textGradient(value: any) {
  return {
    background: `-webkit-linear-gradient(${value})`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };
}

export function filterStyles(value: any) {
  return {
    filter: value,
    WebkitFilter: value,
    MozFilter: value,
  };
}

export const hideScrollbarY = {
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  overflowY: "scroll",
  "&::-webkit-scrollbar": {
    display: "none",
  },
};

export const hideScrollbarX = {
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  overflowX: "scroll",
  "&::-webkit-scrollbar": {
    display: "none",
  },
};

export const getIOSSwitchStyle = (theme: any) => ({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.mode === "dark" ? "#2ECA45" : "#65C466",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === "light" ? "#E9E9EA" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
});

export const shadow = {
  boxShadow: `0 0 20px ${PRIMARY.lighter}`,
  borderRadius: "12px",
};

export const lineBreak = {
  wordWrap: "break-word",
  whiteSpace: "normal",
  wordBreak: "break-word",
  // overflowWrap: "anywhere",
};

export const buttonSuccess = {
  backgroundColor: SUCCESS.dark,
  "&:hover": {
    backgroundColor: SUCCESS.darker,
  },
  boxShadow: `0px 0px 17px -6px ${SUCCESS.dark}`,
};

export const buttonError = {
  backgroundColor: ERROR.dark,
  "&:hover": {
    backgroundColor: ERROR.darker,
  },
  boxShadow: `0px 0px 17px -6px ${ERROR.dark}`,
};

export const buttonInfoDark = {
  backgroundColor: INFO.dark,
  "&:hover": {
    backgroundColor: INFO.darker,
  },
  boxShadow: `0px 0px 17px -6px ${INFO.dark}`,
};

export const buttonCancelText = {
  color: ERROR.main,
  "&:hover": {
    color: ERROR.dark,
  },
};

export const blinkInAnimation = keyframes`
   0% {
    box-shadow: 0 0 20px 5px rgba(255, 0, 0, 0.3), inset 0 0 25px 5px #FF0000;
    backdrop-filter: blur(5px);
  }
  50% {
    box-shadow: 0 0 5px 1px rgba(255, 0, 0, 0.1), inset 0 0 5px 1px rgba(148, 7, 7, 0.2);
  }
  100% {
    box-shadow: 0 0 20px 5px rgba(255, 0, 0, 0.3), inset 0 0 25px 5px #FF0000;
    backdrop-filter: blur(5px);
  }
`;

export const blinkOutAnimation = keyframes`
   0% { 
    box-shadow: 0 0 20px 5px rgba(255, 0, 0, 0.3), 0 0 25px 5px #FF0000;    
    backdrop-filter: blur(5px);
  }
  50% { 
    box-shadow: 0 0 5px 1px rgba(255, 0, 0, 0.1), 0 0 5px 1px rgba(148, 7, 7, 0.2);    
  }
  100% { 
    box-shadow: 0 0 20px 5px rgba(255, 0, 0, 0.3), 0 0 25px 5px #FF0000;    
    backdrop-filter: blur(5px);
  }
`;

export const StyledSearch = styled(TextField)(({ theme, type }: any) => ({
  "& .MuiOutlinedInput-root": {
    width: 150,
    marginRight: "10px",
    transition: theme.transitions.create(["box-shadow", "width"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shorter,
    }),
    "&.Mui-focused": {
      width: type === "date" ? 150 : 320,
      boxShadow: theme.customShadows.z8,
    },
    "& fieldset": {
      borderWidth: `1px !important`,
      borderColor: `${alpha(
        theme.palette.grey[500],
        VALUES.FIELD_BORDER_OPACITY,
      )} !important`,
    },
  },
}));

export const StyledDatePicker = styled(TextField)(({ theme }: any) => ({
  "& .MuiOutlinedInput-root": {
    width: 150,
    marginRight: "10px",
    transition: theme.transitions.create(["box-shadow", "width"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shorter,
    }),
    // "&.Mui-focused": {
    //   width: 320,
    //   boxShadow: theme.customShadows.z8,
    // },
    "& fieldset": {
      borderWidth: `1px !important`,
      borderColor: `${alpha(
        theme.palette.grey[500],
        VALUES.FIELD_BORDER_OPACITY,
      )} !important`,
    },
  },
}));
